import { useEffect, useState } from "react";
import { getArtistCalendar } from "../../../api/artistApi";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const generateMonthDays = (year, month) => {
  const days = [];
  const lastDay = new Date(year, month, 0).getDate();
  for (let i = 1; i <= lastDay; i++) {
    const day = String(i).padStart(2, "0");
    days.push(`${year}-${String(month).padStart(2, "0")}-${day}`);
  }
  return days;
};

// How many blank cells before the 1st of the month
const getStartOffset = (year, month) => {
  return new Date(year, month - 1, 1).getDay();
};

const BookingCalendar = ({ artistId, selectedDate, setSelectedDate }) => {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const [year] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [calendarData, setCalendarData] = useState({});
  const [hoverInfo, setHoverInfo] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);

  const fetchCalendar = async () => {
    const data = await getArtistCalendar(artistId, year, month);
    const mapped = {};
    (data.bookings || []).forEach((booking) => {
      if (!mapped[booking.date]) mapped[booking.date] = [];
      mapped[booking.date].push(booking);
    });
    setCalendarData(mapped);
  };

  useEffect(() => {
    artistId && fetchCalendar();
  }, [artistId, year, month]);

  const days = generateMonthDays(year, month);
  const offset = getStartOffset(year, month);

  const monthLabel = new Date(year, month - 1).toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="bg-slate-900/60 rounded-2xl p-5 select-none">

      {/* ── Month navigation ── */}
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={() => setMonth((m) => (m === 1 ? 12 : m - 1))}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition text-lg leading-none"
        >
          ‹
        </button>
        <h2 className="text-sm font-semibold text-white tracking-wide">{monthLabel}</h2>
        <button
          onClick={() => setMonth((m) => (m === 12 ? 1 : m + 1))}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition text-lg leading-none"
        >
          ›
        </button>
      </div>

      {/* ── Day headers ── */}
      <div className="grid grid-cols-7 mb-2">
        {DAYS.map((d, i) => (
          <div key={i} className="text-center text-[10px] uppercase tracking-widest text-slate-600 font-medium py-1">
            {d}
          </div>
        ))}
      </div>

      {/* ── Date grid ── */}
      <div className="grid grid-cols-7 gap-1">
        {/* Blank offset cells */}
        {Array.from({ length: offset }).map((_, i) => (
          <div key={`blank-${i}`} />
        ))}

        {days.map((date) => {
          const bookings = calendarData[date] || [];
          const isSelected = selectedDate === date;
          const isBooked = bookings.length > 0;
          const isToday = date === todayStr;

          return (
            <button
              key={date}
              onClick={() => setSelectedDate(date)}
              onMouseEnter={() => {
                if (bookings.length) {
                  setHoverInfo(bookings[0]);
                  setHoverDate(date);
                }
              }}
              onMouseLeave={() => {
                setHoverInfo(null);
                setHoverDate(null);
              }}
              className={`relative h-9 rounded-lg text-xs font-medium flex items-center justify-center transition-all
                ${isSelected
                  ? "bg-pink-600 text-white shadow-lg shadow-pink-900/40"
                  : isBooked
                  ? "bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30"
                  : isToday
                  ? "border border-white/20 text-white hover:bg-slate-800"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
            >
              {date.split("-")[2]}
              {/* Today dot */}
              {isToday && !isSelected && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-pink-500" />
              )}
              {/* Booked dot */}
              {isBooked && !isSelected && (
                <span className="absolute top-1 right-1 w-1 h-1 rounded-full bg-amber-400" />
              )}
            </button>
          );
        })}
      </div>

      {/* ── Legend ── */}
      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-white/5">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-pink-600 inline-block" />
          <span className="text-[10px] text-slate-500">Selected</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-500/20 border border-amber-500/30 inline-block" />
          <span className="text-[10px] text-slate-500">Booked</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm border border-white/20 inline-block" />
          <span className="text-[10px] text-slate-500">Today</span>
        </div>
      </div>

      {/* ── Hover booking info ── */}
      {hoverInfo && (
        <div className="mt-3 bg-slate-800/60 border border-white/8 rounded-xl px-4 py-3 space-y-1.5">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-2">Booking Info</p>
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">Promoter</span>
            <span className="text-slate-200">{hoverInfo.promoter?.company_name ?? "—"}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">Status</span>
            <span className="text-slate-200 capitalize">{hoverInfo.status ?? "—"}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">Fee</span>
            <span className="text-slate-200">{hoverInfo.agreed_fee ?? "—"}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCalendar;